import React, { useState } from 'react';

interface DownloadPanelProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  logs?: any[]; // Add logs prop to receive actual data
}

const DownloadPanel: React.FC<DownloadPanelProps> = ({ 
  isOpen, 
  onClose, 
  groupId,
  logs = [] // Default to empty array
}) => {
  const [downloadFormat, setDownloadFormat] = useState<'csv' | 'json'>('csv');

  const handleDownload = async () => {
    try {
      if (!logs || logs.length === 0) {
        alert('No logs available for download');
        return;
      }

      let downloadData: string;
      
      if (downloadFormat === 'csv') {
        // Create CSV header and data from actual logs
        const headers = ['ID', 'User', 'Action', 'Resource Type', 'Timestamp', 'Details', 'IP Address'];
        const csvData = [headers.join(',')];
        
        // Add actual log data
        logs.forEach(log => {
          const row = [
            log.id || '',
            log.user_id || '',
            log.action || '',
            log.resource_type || '',
            log.timestamp || '',
            log.details || '',
            log.ip_address || ''
          ].map(field => `"${field}"`).join(',');
          csvData.push(row);
        });
        
        downloadData = csvData.join('\n');
      } else {
        // Create JSON data from actual logs
        const jsonData = logs.map(log => ({
          id: log.id,
          user_id: log.user_id,
          action: log.action,
          resource_type: log.resource_type,
          timestamp: log.timestamp,
          details: log.details,
          ip_address: log.ip_address
        }));
        
        downloadData = JSON.stringify(jsonData, null, 2);
      }
      
      // Create blob and download
      const blob = new Blob([downloadData], {
        type: downloadFormat === 'csv' ? 'text/csv' : 'application/json',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${groupId}-${new Date().toISOString().split('T')[0]}.${downloadFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      onClose();
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Download Audit Logs</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Download Format
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="csv"
                    checked={downloadFormat === 'csv'}
                    onChange={(e) => setDownloadFormat(e.target.value as 'csv' | 'json')}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">CSV</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="json"
                    checked={downloadFormat === 'json'}
                    onChange={(e) => setDownloadFormat(e.target.value as 'csv' | 'json')}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">JSON</span>
                </label>
              </div>
            </div>

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Download Information
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>This will download {logs.length} audit log entries in {downloadFormat.toUpperCase()} format.</p>
                    <p className="mt-1">The file will be named: audit-logs-{groupId}-{new Date().toISOString().split('T')[0]}.{downloadFormat}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleDownload}
              disabled={!logs || logs.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Download
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DownloadPanel;
